import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
  Prompt,
} from "react-router-dom";
import FormDialogProvider from "Components/context/FormDialogContext";
import ScrollToTop from "Components/ScrollToTop";
import StickyBanner from "Components/StickyBanner";
import LoadingIndicator from "Components/LoadingIndicator";
import BusinessRoutes from "./BusinesRoutes";
import StoreDirectoryRoutes from "./StoreDirectoryRoutes";
import TipsAndTutorialsRoutes from "./TipsAndTutorialsRoutes";
import LoansRoutes from "./LoansRoutes";
import AboutRoutes from "./AboutRoutes";
import SupportAndSecurityRoutes from "./SupportAndSecurityRoutes";
import InvestmentRoutes from "./InvestmentRoutes";
import {
  DEPOSITS_LANDING_PAGE,
  INSTAPAY_PESONET,
  MOBILE_CHECK_DEPOSIT_PAGE,
  FEES_AND_CHARGES,
  DXP_LANDING_PAGE,
  OTHER_SERVICES_EWBILLERS,
  FOREX_RATES_STAND_ALONE_PATH,
} from "./paths";
import AcquiredAssetsRoutes from "./AcquiredAssetsRoutes";
import RequestForCallbackRoutes from "./RequestForCallbackRoutes";
import MyDialog from "Components/MyDialog";
import useRouterPreNavigationMiddleware from "./useRouterPreNavigationMiddleware";
import NavigatorPrototye from "Components/NavigatorPrototype";
import SitemapPrototype from "Components/SitemapPrototype";
import { Hidden } from "@material-ui/core";
import BottomNavigator from "Components/BottomNavigator";
import useHolidayBanner from "hooks/useHolidayBanner";
import useNavigationAndFooterData from "hooks/useNavigationAndFooterData";

//#region  PAGES
const PromoExpired = lazy(() => import("Components/CreditCards/PromoExpired"));
const PrivacyStatement = lazy(() => import("Components/PrivacyStatement"));
const Homepage = lazy(() => import("pages/Home"));
const CreditCard = lazy(() => import("Components/CreditCards/CreditCard"));
const CreditCardInner = lazy(() =>
  import("Components/CreditCards/CreditCardInner")
);
const CCPage = lazy(() =>
  import("Components/CreditCards/CreditCardProductPages/CCPage")
);
const HistoryNavpu = lazy(() => import("pages/HistoryNavpu"));
const PerformanceCalculator = lazy(() => import("pages/PerformanceCalculator"));
const FeesAndCharges = lazy(() => import("pages/FeesAndCharges"));
const Forex = lazy(() => import("pages/ForexStandalone"));
const Promo = lazy(() => import("Components/CreditCards/Promo"));
const PromoInner = lazy(() => import("Components/CreditCards/PromoInner"));
const Deposits = lazy(() => import("Components/Deposits/Deposits"));
const CardsInner = lazy(() => import("Components/DebitandPrepaid/CardsInner"));
const ContactUs = lazy(() => import("Components/ContactUs"));
const DepositsInner = lazy(() => import("Components/Deposits/DepositsInner"));
const DXPPLanding = lazy(() => import("pages/DXPLanding"));
const EwBillers = lazy(() => import("pages/Business/EwBillers"));
const Forms = lazy(() => import("Components/Forms"));
const MobileCheckDepositLandingPage = lazy(() =>
  import("pages/MobileCheckDeposit")
);
const ListiclePage = lazy(() => import("Components/ListiclePage"));
const TDThanks = lazy(() => import("Components/TDThanks"));
const TermsOfUse = lazy(() => import("Components/TermsOfUse"));
const CCThankYou = lazy(() => import("Components/ThankYouPages/CCThankYou"));
const LGThankYou = lazy(() => import("Components/ThankYouPages/LGThankYou"));
const TDThankYou = lazy(() => import("Components/ThankYouPages/TDThankYou"));
const Instapaypesonet = lazy(() => import("Components/Instapaypesonet"));
const Rates = lazy(() => import("pages/Rates"));
const Advisories = lazy(() => import("Components/Advisories/Advisories"));
const AdvisoriesInner = lazy(() =>
  import("Components/Advisories/AdvisoriesInner")
);
const EastWestUITFProducts = lazy(() => import("pages/EastWestUITFProducts"));
const EasyWay = lazy(() => import("Components/EasyWay"));
const Standalone = lazy(() => import("Components/StandalonePage"));
const HomePrototype = lazy(() => import("pages/HomePrototype"));
const BulkTransactionConverter = lazy(() =>
  import("Components/Standalone/BulkTransactionConverter")
);

//#endregion
const INITIAL_PADDING_TOP = 87;
const CLOSING_PADDING_TOP = 26;

const Routes = () => {
  const {
    field_hbanner_short_description,
    field_imageholiday,
    field_holiday_banner_link,
  } = useHolidayBanner();

  const { openInsuranceDialog, closeInsuranceDialog, handleRoutes } =
    useRouterPreNavigationMiddleware();
  const { data, loading, rightMenuData, footer_sitemap } =
    useNavigationAndFooterData();

  return (
    <>
      <Router>
        <MyDialog
          open={openInsuranceDialog}
          closeDialog={closeInsuranceDialog}
        />
        <Prompt message={handleRoutes} />
        <NavigatorPrototye
          data={data}
          loading={loading}
          rightMenuData={rightMenuData}
        />
        <div className="mainContent">
          {field_imageholiday != "" && (
            <StickyBanner
              link={field_holiday_banner_link}
              image={field_imageholiday}
              description={field_hbanner_short_description}
            />
          )}

          <div
            style={{
              paddingTop:
                field_imageholiday === ""
                  ? CLOSING_PADDING_TOP
                  : INITIAL_PADDING_TOP,
            }}
          >
            <ScrollToTop>
              <Suspense fallback={<LoadingIndicator isLoading={true} />}>
                <FormDialogProvider>
                  <Switch>
                    <Route path="/pages/:id" component={Standalone} />
                    <Route path="/easyway" component={EasyWay} />
                    <Route
                      path={FOREX_RATES_STAND_ALONE_PATH}
                      component={Forex}
                    />
                    <Route
                      path={"/cash-management-converter-online-form"}
                      component={BulkTransactionConverter}
                    />
                    <Route path="/advisories/:id" component={AdvisoriesInner} />
                    <Route path="/advisories" component={Advisories} />
                    <Route path={DXP_LANDING_PAGE} component={DXPPLanding} />
                    <Route
                      path={OTHER_SERVICES_EWBILLERS}
                      component={EwBillers}
                    />
                    <Route path="/interestrates" component={Rates} />
                    <Route path="/Thank-You" component={TDThanks} />
                    <Route
                      path="/deposits/timedeposit/thankyou"
                      component={TDThankYou}
                    />
                    <Route path={FEES_AND_CHARGES} component={FeesAndCharges} />
                    <Route
                      path="/creditcards/thankyou"
                      component={CCThankYou}
                    />
                    <Route path="/callback/thankyou" component={LGThankYou} />
                    <Route path="/termsofuse" component={TermsOfUse} />
                    <Route
                      path="/privacystatement"
                      component={PrivacyStatement}
                    />
                    <Route
                      path={INSTAPAY_PESONET}
                      component={Instapaypesonet}
                    />
                    <Route path="/cards/creditcards/:id" component={CCPage} />
                    <Route path="/cards/:id" component={CardsInner} />
                    <Route
                      path="/deposits/timedeposit"
                      render={(props) => (
                        <TimeDeposit {...props} urlFab="deposit" />
                      )}
                    />
                    <Route path="/deposits/:id" component={DepositsInner} />
                    <Route path={DEPOSITS_LANDING_PAGE} component={Deposits} />
                    <Route
                      path="/creditcards/downloadableforms"
                      component={ListiclePage}
                    />
                    <Route
                      path="/CreditCards/:id"
                      component={CreditCardInner}
                    />
                    <Route path="/CreditCards" component={CreditCard} />
                    <Route
                      path="/forms/timedeposit/:referenceid"
                      component={Forms}
                    />
                    <Route path="/forms" component={Forms} />
                    <Route path="/contactus" component={ContactUs} />
                    <Route exact path="/Promos/:id" component={PromoInner} />
                    <Route path="/Promos#:id" component={Promo} />
                    <Route path="/Promos" component={Promo} />
                    <Route
                      exact
                      path="/promo-expired"
                      component={PromoExpired}
                    />
                    {/* <Route
                        path="/history-navpu"
                        component={HistoryNavpu}
                      />
                  <Route
                        path="/performance-calculator"
                        component={PerformanceCalculator}
                      /> */}
                    <Route
                      path="/request-for-callback"
                      component={RequestForCallbackRoutes}
                    />
                    <Route
                      path={MOBILE_CHECK_DEPOSIT_PAGE}
                      component={MobileCheckDepositLandingPage}
                    />

                    <Route path="/investments" component={InvestmentRoutes} />
                    <Route
                      path="/investmentProduct/compare-uitf-products"
                      component={EastWestUITFProducts}
                    />
                    <Route
                      path="/acquired-assets"
                      component={AcquiredAssetsRoutes}
                    />
                    <Route path="/business" component={BusinessRoutes} />
                    <Route
                      path="/supportandsecurity"
                      component={SupportAndSecurityRoutes}
                    />
                    <Route
                      path="/storedirectory"
                      component={StoreDirectoryRoutes}
                    />
                    <Route path="/loans" component={LoansRoutes} />
                    <Route path="/about" component={AboutRoutes} />
                    <Route
                      path="/tipsandtutorials"
                      component={TipsAndTutorialsRoutes}
                    />
                    <Route exact path="/" component={HomePrototype} />
                    {/* <Route exact path="/" component={Homepage} /> */}
                    <Route path="*" component={() => <Redirect to="/" />} />
                  </Switch>
                </FormDialogProvider>
              </Suspense>
            </ScrollToTop>
            <Hidden smDown>
              <footer>
                <SitemapPrototype footer_links={footer_sitemap} />
              </footer>
            </Hidden>
            <BottomNavigator />
          </div>
        </div>

        {/* </Navigation> */}
      </Router>
    </>
  );
};

export default Routes;
